import { Head, Link } from "@inertiajs/react";
import { ArrowLeft, Edit, Package, Users } from "lucide-react";

import { SubcategoryList } from "@/components/subcategory-list";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import AppLayout from "@/layouts/app-layout";
import { categoriesPath, editCategoryPath } from "@/routes";
import type { BreadcrumbItem, Category } from "@/types";

interface CategoryShowProps {
  category: Category & {
    subcategories: {
      id: number;
      name: string;
      description?: string;
      created_at: string;
    }[];
    suppliers: {
      id: number;
      name: string;
    }[];
  };
}

export default function CategoryShow({ category }: CategoryShowProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Categorías",
      href: categoriesPath(),
    },
    {
      title: category.name,
      href: "#",
    },
  ];

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${category.name} - Categorías`} />
      <div className="flex h-full flex-1 flex-col gap-6 p-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link href={categoriesPath()}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{category.name}</h1>
              {category.description && (
                <p className="text-muted-foreground">{category.description}</p>
              )}
            </div>
          </div>
          <Button asChild>
            <Link href={editCategoryPath(category)}>
              <Edit className="h-4 w-4 mr-2" />
              Editar Categoría
            </Link>
          </Button>
        </div>

        {/* Statistics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Subcategorías
              </CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {category.subcategories?.length || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                subcategorías activas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Proveedores</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {category.suppliers?.length || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                proveedores asignados
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Subcategories Management */}
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold">Subcategorías</h2>
            <p className="text-muted-foreground">
              Administra las subcategorías para esta categoría
            </p>
          </div>

          <Separator />

          <SubcategoryList
            category={category}
            subcategories={category.subcategories || []}
          />
        </div>

        {/* Associated Suppliers */}
        {category.suppliers && category.suppliers.length > 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold">Proveedores Asociados</h2>
              <p className="text-muted-foreground">
                Proveedores que pertenecen a esta categoría
              </p>
            </div>

            <Separator />

            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {category.suppliers.map((supplier) => (
                <Card key={supplier.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{supplier.name}</CardTitle>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
